import type { CentralChatIssueAttachment } from './types';

/**
 * One MJ session per PAGE, not one app-wide thread. `key` is the storage scope
 * for that page's session; `label` is a short human name for the history list,
 * so it reads which page a conversation belongs to.
 *
 * Rule: an issue-bound chat keys on the issue's client PAGE (its WordPress id,
 * else page title, else the issue id) so every issue on the same page shares one
 * session. Everything else keys on the route: pathname plus any identifying id
 * (content/template/section). Volatile query params are ignored — the planner's
 * ?view=table and ?view=calendar are one page — because only pathname + those
 * ids go into the key.
 */
export interface ActivePage {
  key: string;
  label: string;
}

type RouteFields = {
  route?: string;
  content_id?: string;
  template_id?: string;
  section_id?: string;
};

const ROUTE_LABELS: Array<[string, string]> = [
  ['/audit/technical-issues', 'Technical Audit'],
  ['/audit/technical', 'Technical Audit'],
  ['/audit', 'Audit'],
  ['/content/templates', 'Template Builder'],
  ['/content/planner', 'Content Planner'],
  ['/content/editor', 'Editor'],
  ['/editor', 'Editor'],
  ['/blog', 'Blog'],
  ['/pages/service', 'Service Page'],
  ['/pages/generate', 'Service Page'],
  ['/chat', 'Chat'],
  ['/dashboard', 'Dashboard'],
  ['/seo', 'SEO'],
];

function normalizePath(path: string): string {
  const trimmed = path.split(/[?#]/)[0].replace(/\/+$/, '');
  return trimmed || '/';
}

function routeLabel(pathname: string): string {
  for (const [prefix, label] of ROUTE_LABELS) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return label;
  }
  const segment = pathname.split('/').filter(Boolean)[0];
  if (!segment) return 'Home';
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/[-_]/g, ' ');
}

/** The client page a technical-audit issue lives on. */
export function issuePage(
  issue: Pick<CentralChatIssueAttachment, 'wordpress_id' | 'page_title' | 'issue_id'>,
): ActivePage {
  const id =
    issue.wordpress_id != null
      ? `wp:${issue.wordpress_id}`
      : issue.page_title
        ? `title:${issue.page_title}`
        : `issue:${issue.issue_id}`;
  return { key: `issue:${id}`, label: issue.page_title || 'Audit issue page' };
}

/** A Core route page: pathname + any identifying id, volatile params dropped. */
export function routePage(context: RouteFields, pathname: string): ActivePage {
  const base = normalizePath(context.route || pathname);
  const ids = [context.content_id, context.template_id, context.section_id].filter(
    (value): value is string => Boolean(value),
  );
  const key = `route:${base}${ids.length ? `#${ids.join(':')}` : ''}`;
  return { key, label: routeLabel(base) };
}

/** The page whose session is live right now. Issue binding wins over route. */
export function resolvePageSession(input: {
  activeIssue?: CentralChatIssueAttachment | null;
  context: RouteFields;
  pathname: string;
}): ActivePage {
  if (input.activeIssue) return issuePage(input.activeIssue);
  return routePage(input.context, input.pathname);
}

/**
 * The page a persisted conversation belongs to, derived from the fields the
 * backend returns. Issue-bound conversations carry no page id here, so they
 * resolve to their route only — the live client-side page map is the accurate
 * source for those, and this is the fallback for route pages made elsewhere.
 */
export function conversationPage(conv: {
  route?: string | null;
  content_id?: string | null;
  template_id?: string | null;
  section_id?: string | null;
}): ActivePage | null {
  if (!conv.route && !conv.content_id && !conv.template_id && !conv.section_id) {
    return null;
  }
  return routePage(
    {
      route: conv.route ?? undefined,
      content_id: conv.content_id ?? undefined,
      template_id: conv.template_id ?? undefined,
      section_id: conv.section_id ?? undefined,
    },
    conv.route ?? '/',
  );
}

/** The per-page storage scope: tenant/user scope joined to the page key. */
export function pageScopeKey(sessionKey: string | null, pageKey: string): string | null {
  return sessionKey ? `${sessionKey}::${pageKey}` : null;
}
